// src/components/RootErrorBoundary.tsx
// Top-level boundary that catches any render error in the app so the user
// sees a debuggable card instead of a black screen (React #130 otherwise
// surfaces only as "Minified React error #130" in console).
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode; }
interface State { error: Error | null; info: ErrorInfo | null; }

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };
  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log with full info; componentStack has the unminified React tree.
    console.error('Root error boundary caught:', error);
    console.error('Component stack:', info.componentStack);
    this.setState({ error, info });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen bg-[#0c0d14] text-white p-6">
        <div className="max-w-3xl mx-auto mt-10 rounded-xl border border-red-500/40 bg-red-500/5 p-6 space-y-3">
          <h1 className="text-red-300 font-bold text-lg">⚠ Error de render en el dashboard</h1>
          <p className="text-white/60 text-sm">
            Captura esto tal cual y mándalo a desarrollo — incluye el texto abajo.
            Si la ruta estaba en el URL, también pega la URL.
          </p>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Mensaje</div>
            <pre className="text-xs text-white/80 whitespace-pre-wrap break-all bg-black/40 p-2 rounded">{this.state.error?.message}</pre>
          </div>
          {this.state.error?.stack && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Stack</div>
              <pre className="text-[10px] text-white/60 whitespace-pre-wrap break-all bg-black/40 p-2 rounded max-h-60 overflow-auto">{this.state.error.stack}</pre>
            </div>
          )}
          {this.state.info?.componentStack && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Component stack</div>
              <pre className="text-[10px] text-white/60 whitespace-pre-wrap break-all bg-black/40 p-2 rounded max-h-60 overflow-auto">{this.state.info.componentStack}</pre>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => this.setState({ error: null, info: null })}
              className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white/80 text-xs"
            >
              Reintentar
            </button>
            <button
              onClick={() => { try { window.location.href = '/dashboard/'; } catch {} }}
              className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white/80 text-xs"
            >
              Ir al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }
}
